import { useState, useEffect, useMemo } from 'react'
import { apiClient } from '../utils/api'
import { backendClient } from '../utils/backend'
import { usePlayerStore } from '../stores'
import {
  deleteUserAndData,
  updateUserRole,
  manageExtraRole,
  verifyUser,
  managePlayerVisibility,
  manageTopTalent,
} from '../utils/adminApi'
import {
  Users,
  Shield,
  Trash2,
  UserCog,
  Mail,
  Search,
  BadgeCheck,
  Building2,
  Activity,
} from 'lucide-react'

interface Profile {
  id: string
  email: string
  full_name?: string
  role: 'scout' | 'club' | 'admin' | 'sub_admin'
  created_at: string
  is_verified?: boolean
}

type RoleValue = 'scout' | 'club' | 'admin' | 'sub_admin'

interface ClubRecord {
  id: string
  name: string
  scout_id?: string | null
  is_verified?: boolean
}

interface UserRoleRow {
  user_id: string
  role: RoleValue
}

export default function AdminAccounts() {
  const { players, fetchPlayers } = usePlayerStore()
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [userRoles, setUserRoles] = useState<UserRoleRow[]>([])
  const [clubs, setClubs] = useState<ClubRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | 'scout' | 'club' | 'admin' | 'sub_admin'>('all')
  const [bulkUpdatingAccountId, setBulkUpdatingAccountId] = useState<string | null>(null)
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null)

  useEffect(() => {
    fetchPlayers()
    fetchProfiles()
  }, [fetchPlayers])

  const fetchProfiles = async () => {
    setLoading(true)
    try {
      // Get all players from the player store
      const allPlayers = players || []
      
      // Convert players to profiles format
      const profileData = allPlayers.map((player: any) => ({
        id: player.id,
        email: player.email || '',
        full_name: player.full_name || player.name,
        role: player.role || 'scout',
        created_at: player.created_at || new Date().toISOString(),
        is_verified: player.is_verified || false
      }))
      
      setProfiles(profileData)
      
      // Fetch roles from backend
      const [roleData, clubData] = await Promise.all([
        backendClient.from('user_roles').select('user_id, role'),
        apiClient.getClubs(),
      ])
      setUserRoles((roleData.data || []) as UserRoleRow[])
      setClubs((clubData || []) as ClubRecord[])
    } catch (error) {
      console.error('Failed to fetch profiles:', error)
    } finally {
      setLoading(false)
    }
  }

  const roleOptions: RoleValue[] = ['scout', 'club', 'admin', 'sub_admin']
  const normalizeClubName = (value?: string | null) => value?.trim().toLowerCase().replace(/\s+/g, ' ') || ''

  const getClubRecordForProfile = (profile: Profile) => {
    if (profile.role !== 'club') return null

    return clubs.find((club) =>
      club.id === profile.id ||
      club.scout_id === profile.id ||
      normalizeClubName(club.name) === normalizeClubName(profile.email.split('@')[0])
    ) || null
  }

  const isProfileVerified = (profile: Profile) => {
    const linkedClub = getClubRecordForProfile(profile)
    if (linkedClub) return linkedClub.is_verified === true
    return Boolean(profile.is_verified)
  }

  const canVerifyProfile = (profile: Profile) => {
    return profile.role === 'club' || profile.is_verified !== undefined
  }

  const getExtraRoles = (userId: string, primaryRole: RoleValue) => {
    return userRoles
      .filter((row) => row.user_id === userId && row.role !== primaryRole)
      .map((row) => row.role)
  }

  const refreshRoles = async () => {
    const { data } = await backendClient.from('user_roles').select('user_id, role')
    setUserRoles((data || []) as UserRoleRow[])
  }

  const handleRoleChange = async (id: string, newRole: string) => {
    if (!confirm(`Change role to ${newRole === 'scout' ? 'Scout' : newRole.toUpperCase()}?`)) return
    try {
      const result = await updateUserRole(id, newRole)
      if (result.ok) {
        await fetchProfiles()
      } else {
        alert('Failed to update role: ' + (result.error || 'Unknown error'))
      }
    } catch (error) {
      alert('Failed to update role')
    }
  }

  const handleGrantExtraRole = async (userId: string, role: RoleValue) => {
    try {
      const result = await manageExtraRole(userId, role, 'grant')
      if (result.ok) {
        await refreshRoles()
      } else {
        alert('Failed to grant role: ' + (result.error || 'Unknown error'))
      }
    } catch (error) {
      alert('Failed to grant role')
    }
  }

  const handleRevokeExtraRole = async (userId: string, role: RoleValue) => {
    if (!confirm(`Remove ${role.toUpperCase()} from this user?`)) return
    try {
      const result = await manageExtraRole(userId, role, 'revoke')
      if (result.ok) {
        await refreshRoles()
      } else {
        alert('Failed to remove role: ' + (result.error || 'Unknown error'))
      }
    } catch (error) {
      alert('Failed to remove role')
    }
  }

  const handleDelete = async (id: string) => {
    const choice = confirm(
      'How do you want to handle this account?\n\n' +
      '(OK) Archive: Hide all players but keep the account\n' +
      '(Cancel) Return to menu'
    )
    
    if (!choice) return
    
    // Offer hard delete option
    const hardDelete = confirm(
      'DELETE ACCOUNT & ALL DATA?\n\n' +
      'This will:\n' +
      '- Remove all players by this account\n' +
      '- Delete the profile\n' +
      '- Remove the auth user\n\n' +
      'This is PERMANENT and cannot be undone.\n' +
      'Click OK to confirm hard delete, Cancel to archive instead.'
    )

    const mode: 'archive' | 'delete' = hardDelete ? 'delete' : 'archive'

    setDeletingAccountId(id)
    try {
      const result = await deleteUserAndData(id, mode)
      if (result.ok) {
        alert(`User and data ${result.mode}d successfully.`)
        await fetchProfiles()
      } else {
        alert(`Failed: ${result.error}`)
      }
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setDeletingAccountId(null)
    }
  }

  const getAccountPlayerIds = (profile: Profile) => {
    const linkedClub = getClubRecordForProfile(profile)
    const linkedClubName = normalizeClubName(linkedClub?.name)

    return players
      .filter((player) => {
        if (profile.role === 'club') {
          if (player.owner_club_id === profile.id) return true
          if (linkedClub?.id && player.owner_club_id === linkedClub.id) return true
          if (linkedClub?.scout_id && player.owner_club_id === linkedClub.scout_id) return true
          return Boolean(linkedClubName) && normalizeClubName(player.club) === linkedClubName
        }

        return player.scout === profile.email
      })
      .map((player) => player.id)
  }

  const handleVerify = async (profile: Profile, isVerified: boolean) => {
    try {
      const result = await verifyUser(profile.id, isVerified)
      if (result.ok) {
        await fetchProfiles()
      } else {
        alert('Failed to verify account: ' + (result.error || 'Unknown error'))
      }
    } catch (error) {
      alert('Failed to verify account')
    }
  }

  const handleBulkVisibility = async (profile: Profile, isPublic: boolean) => {
    const playerIds = getAccountPlayerIds(profile)
    if (!playerIds.length) {
      alert('No players were found for this account.')
      return
    }

    setBulkUpdatingAccountId(profile.id)
    try {
      const result = await managePlayerVisibility(playerIds, isPublic)
      if (result.ok) {
        alert(`${result.updated} player profiles were updated.`)
        await fetchProfiles()
      } else {
        alert('Failed to update visibility: ' + (result.error || 'Unknown error'))
      }
    } catch (error) {
      alert('Failed to update player visibility for this account.')
    } finally {
      setBulkUpdatingAccountId(null)
    }
  }

  const handleBulkTopTalent = async (profile: Profile, isTop: boolean) => {
    const playerIds = getAccountPlayerIds(profile)
    if (!playerIds.length) {
      alert('No players were found for this account.')
      return
    }

    setBulkUpdatingAccountId(profile.id)
    try {
      const result = await manageTopTalent(playerIds, isTop)
      if (result.ok) {
        alert(`${result.updated || 0} player profiles were updated.`)
        await fetchProfiles()
      } else {
        alert('Failed to update top talent: ' + (result.error || 'Unknown error'))
      }
    } catch (error) {
      alert('Failed to update top talent for this account.')
    } finally {
      setBulkUpdatingAccountId(null)
    }
  }

  const filteredProfiles = useMemo(() => {
    return profiles.filter((p) => {
      const matchesSearch = 
        p.email.toLowerCase().includes(filter.toLowerCase()) ||
        (p.full_name || '').toLowerCase().includes(filter.toLowerCase())
      const matchesRole = roleFilter === 'all' || p.role === roleFilter
      return matchesSearch && matchesRole
    })
  }, [profiles, filter, roleFilter])

  const stats = useMemo(() => {
    const scouts = profiles.filter((p) => p.role === 'scout').length
    const clubs = profiles.filter((p) => p.role === 'club').length
    const admins = profiles.filter((p) => p.role === 'admin').length
    const verified = profiles.filter((p) => isProfileVerified(p)).length
    return { scouts, clubs, admins, verified, total: profiles.length }
  }, [profiles, clubs])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {[
          { label: 'Total Accounts', value: stats.total, icon: Users, color: 'text-green-500' },
          { label: 'Scouts', value: stats.scouts, icon: Activity, color: 'text-blue-400' },
          { label: 'Clubs', value: stats.clubs, icon: Building2, color: 'text-cyan-400' },
          { label: 'Admins', value: stats.admins, icon: Shield, color: 'text-purple-400' },
          { label: 'Verified', value: stats.verified, icon: BadgeCheck, color: 'text-emerald-400' },
        ].map((item) => (
          <div key={item.label} className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest font-black text-gray-500">{item.label}</p>
              <item.icon size={18} className={item.color} />
            </div>
            <p className="text-3xl font-black text-white">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#161b22] border border-gray-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="p-6 border-b border-gray-800 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users size={24} className="text-green-500" />
                Account Management
              </h2>
              <p className="text-sm text-gray-500 mt-1">Search, reassign, verify, and remove scout and club accounts.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search by email..."
                  className="pl-10 pr-4 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white text-sm focus:border-green-500 outline-none w-full sm:w-72"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as any)}
                className="bg-gray-950 border border-gray-800 rounded-lg text-white text-sm px-4 py-2 focus:border-green-500 outline-none"
              >
                <option value="all">All Roles</option>
                <option value="scout">Scout</option>
                <option value="club">Club</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          {/* PREVIOUS DESIGN
          <div className="flex flex-wrap gap-2">
            {(['all', 'scout', 'club', 'admin'] as const).map((role) => (
              <button
                key={role}
                onClick={() => setRoleFilter(role)}
                className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition ${roleFilter === role
                    ? 'bg-green-500/10 border-green-500/30 text-green-400'
                    : 'bg-gray-950 border-gray-800 text-gray-500 hover:text-white'
                  }`}
              >
                {role === 'all' ? 'All Accounts' : role === 'scout' ? 'Scout' : role.toUpperCase()}
              </button>
            ))}
          </div>
          */}

          {/* NEW OPTIMIZED DESIGN */}
          <div className="flex gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar p-1 bg-gray-950/50 border border-gray-800 rounded-2xl w-fit max-w-full">
{['all', 'scout', 'club', 'admin', 'sub_admin'].map((_, i) => {
              const role = ['all', 'scout', 'club', 'admin', 'sub_admin'][i] as 'all' | 'scout' | 'club' | 'admin' | 'sub_admin'
              return (
                <button
                  key={role}
                  onClick={() => setRoleFilter(role)}
                  className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all shrink-0 ${roleFilter === role ? 'bg-[#7cff01]/10 shadow-[inset_0_0_0_1px_rgba(124,255,1,0.2)] text-[#7cff01]' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                  title={role === 'all' ? 'All Accounts' : role === 'scout' ? 'Scout' : role === 'sub_admin' ? 'Sub-Admin' : role.toUpperCase()}
                >
                  {role === 'all' ? 'All Accounts' : role === 'scout' ? 'Scout' : role === 'sub_admin' ? 'Sub-Admin' : role.toUpperCase()}
                </button>
              )
            })}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading accounts...</div>
        ) : filteredProfiles.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No accounts found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-950/50 border-b border-gray-800">
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">User</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Role</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Joined</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredProfiles.map((profile) => (
                  <tr key={profile.id} className="hover:bg-gray-800/30 transition-colors">
<td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-500">
                          <Mail size={18} />
                        </div>
                        <div>
                          <p className="text-white font-semibold">{profile.full_name || profile.email}</p>
                          <p className="text-[10px] text-gray-500 tracking-tighter">{profile.email}</p>
                          <p className="text-[10px] text-gray-400">{profile.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${profile.role === 'admin' ? 'bg-purple-900/10 border-purple-800 text-purple-400' :
                          profile.role === 'club' ? 'bg-blue-900/10 border-blue-800 text-blue-400' :
                            'bg-green-900/10 border-green-800 text-green-400'
                        }`}>
                        {profile.role === 'admin' && <Shield size={12} />}
                        {profile.role === 'scout' ? 'Scout' : profile.role.toUpperCase()}
                      </div>
                      {getExtraRoles(profile.id, profile.role).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {getExtraRoles(profile.id, profile.role).map((role) => (
                            <span
                              key={role}
                              className="px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-gray-950 border-gray-800 text-gray-400"
                            >
                              Extra {role}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${isProfileVerified(profile)
                          ? 'bg-emerald-900/10 border-emerald-800/30 text-emerald-400'
                          : 'bg-gray-900 border-gray-800 text-gray-500'
                        }`}>
                        {isProfileVerified(profile) ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {new Date(profile.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {/* PREVIOUS DESIGN
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => handleBulkVisibility(profile, true)}
                          disabled={bulkUpdatingAccountId === profile.id || getAccountPlayerIds(profile).length === 0}
                          className="px-3 py-2 text-[10px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-800/30 bg-emerald-900/10 hover:bg-emerald-900/20 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Make every player from this account public"
                        >
                          Make All Public
                        </button>
                        <button
                          onClick={() => handleBulkVisibility(profile, false)}
                          disabled={bulkUpdatingAccountId === profile.id || getAccountPlayerIds(profile).length === 0}
                          className="px-3 py-2 text-[10px] font-black uppercase tracking-widest text-red-400 border border-red-800/30 bg-red-900/10 hover:bg-red-900/20 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Make every player from this account private"
                        >
                          Make All Private
                        </button>
                        {canVerifyProfile(profile) && (
                          <button
                            onClick={() => handleVerify(profile, !isProfileVerified(profile))}
                            className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition border ${
                              isProfileVerified(profile)
                                ? 'text-emerald-400 border-emerald-800/30 bg-emerald-900/10 hover:bg-emerald-900/20'
                                : 'text-gray-400 border-gray-800 bg-gray-950 hover:text-emerald-400 hover:bg-emerald-900/10'
                            }`}
                            title={isProfileVerified(profile) ? 'Remove verification' : 'Verify this account'}
                          >
                            {isProfileVerified(profile) ? 'Unverify' : 'Verify'}
                          </button>
                        )}
                        <div className="relative group">
                          <button
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition"
                            title="Change Role"
                          >
                            <UserCog size={18} />
                          </button>
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 border border-gray-800 rounded-lg shadow-2xl p-2 z-50">
                            <div className="flex flex-col gap-1 w-36">
                              {['scout', 'club', 'admin'].filter((role) => role !== profile.role).map((role) => (
                                <button
                                  key={role}
                                  onClick={() => handleRoleChange(profile.id, role)}
                                  className="text-[10px] font-bold uppercase tracking-widest p-2 hover:bg-gray-800 text-gray-400 hover:text-white text-left rounded"
                                >
                                  Make {role === 'scout' ? 'Scout' : role}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="relative group">
                          <button
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition"
                            title="Manage Extra Roles"
                          >
                            <Shield size={18} />
                          </button>
                          <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 border border-gray-800 rounded-lg shadow-2xl p-2 z-50">
                            <div className="flex flex-col gap-1 w-40">
                              {roleOptions.map((role) => {
                                const hasRole = userRoles.some((row) => row.user_id === profile.id && row.role === role)
                                const isPrimary = profile.role === role
                                if (isPrimary) {
                                  return (
                                    <span
                                      key={role}
                                      className="text-[10px] font-bold uppercase tracking-widest p-2 text-gray-600 rounded"
                                    >
                                      Primary {role}
                                    </span>
                                  )
                                }
                                return hasRole ? (
                                  <button
                                    key={role}
                                    onClick={() => handleRevokeExtraRole(profile.id, role)}
                                    className="text-[10px] font-bold uppercase tracking-widest p-2 hover:bg-gray-800 text-gray-400 hover:text-white text-left rounded"
                                  >
                                    Remove {role}
                                  </button>
                                ) : (
                                  <button
                                    key={role}
                                    onClick={() => handleGrantExtraRole(profile.id, role)}
                                    className="text-[10px] font-bold uppercase tracking-widest p-2 hover:bg-gray-800 text-gray-400 hover:text-white text-left rounded"
                                  >
                                    Add {role}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(profile.id)}
                          disabled={deletingAccountId === profile.id}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-900/10 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title={deletingAccountId === profile.id ? 'Deleting...' : 'Delete Account & Data'}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      */}

                      {/* NEW OPTIMIZED DESIGN */}
                      <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap hide-scrollbar max-w-full pb-1 -mb-1">
                        <button
                          onClick={() => handleBulkVisibility(profile, true)}
                          disabled={bulkUpdatingAccountId === profile.id || getAccountPlayerIds(profile).length === 0}
                          className="shrink-0 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-800/30 bg-emerald-900/10 hover:bg-emerald-900/20 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Make every player from this account public"
                        >
                          All Public
                        </button>
                        <button
                          onClick={() => handleBulkVisibility(profile, false)}
                          disabled={bulkUpdatingAccountId === profile.id || getAccountPlayerIds(profile).length === 0}
                          className="shrink-0 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-red-400 border border-red-800/30 bg-red-900/10 hover:bg-red-900/20 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Make every player from this account private"
                        >
                          All Private
                        </button>
                        <button
                          onClick={() => handleBulkTopTalent(profile, true)}
                          disabled={bulkUpdatingAccountId === profile.id || getAccountPlayerIds(profile).length === 0}
                          className="shrink-0 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[#7cff01] border border-[#7cff01]/30 bg-[#7cff01]/10 hover:bg-[#7cff01]/20 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Mark every player from this account as Top Talent"
                        >
                          All Top
                        </button>
                        {canVerifyProfile(profile) && (
                          <button
                            onClick={() => handleVerify(profile, !isProfileVerified(profile))}
                            className={`shrink-0 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition border ${
                              isProfileVerified(profile)
                                ? 'text-emerald-400 border-emerald-800/30 bg-emerald-900/10 hover:bg-emerald-900/20'
                                : 'text-gray-400 border-gray-800 bg-gray-900 hover:text-emerald-400 hover:bg-emerald-900/10'
                            }`}
                            title={isProfileVerified(profile) ? 'Remove verification' : 'Verify this account'}
                          >
                            {isProfileVerified(profile) ? 'Unverify' : 'Verify'}
                          </button>
                        )}
                        <div className="shrink-0 relative group">
                          <button
                            className="p-1.5 text-gray-400 hover:text-white bg-gray-900 border border-gray-800 hover:bg-gray-800 rounded-xl transition flex items-center justify-center"
                            title="Change Role"
                          >
                            <UserCog size={16} />
                          </button>
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-900 border border-gray-800 rounded-xl shadow-2xl p-1.5 z-50">
                            <div className="flex flex-col gap-1 w-36">
                              {['scout', 'club', 'admin'].filter((role) => role !== profile.role).map((role) => (
                                <button
                                  key={role}
                                  onClick={() => handleRoleChange(profile.id, role)}
                                  className="text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-gray-800 text-gray-400 hover:text-white text-left rounded-lg"
                                >
                                  Make {role === 'scout' ? 'Scout' : role}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 relative group">
                          <button
                            className="p-1.5 text-gray-400 hover:text-white bg-gray-900 border border-gray-800 hover:bg-gray-800 rounded-xl transition flex items-center justify-center"
                            title="Manage Extra Roles"
                          >
                            <Shield size={16} />
                          </button>
                          <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 border border-gray-800 rounded-xl shadow-2xl p-1.5 z-50">
                            <div className="flex flex-col gap-1 w-40">
                              {roleOptions.map((role) => {
                                const hasRole = userRoles.some((row) => row.user_id === profile.id && row.role === role)
                                const isPrimary = profile.role === role
                                if (isPrimary) {
                                  return (
                                    <span
                                      key={role}
                                      className="text-[10px] font-bold uppercase tracking-widest px-3 py-2 text-gray-600 rounded-lg"
                                    >
                                      Primary {role}
                                    </span>
                                  )
                                }
                                return hasRole ? (
                                  <button
                                    key={role}
                                    onClick={() => handleRevokeExtraRole(profile.id, role)}
                                    className="text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-gray-800 text-gray-400 hover:text-white text-left rounded-lg"
                                  >
                                    Remove {role}
                                  </button>
                                ) : (
                                  <button
                                    key={role}
                                    onClick={() => handleGrantExtraRole(profile.id, role)}
                                    className="text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-gray-800 text-gray-400 hover:text-white text-left rounded-lg"
                                  >
                                    Add {role}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(profile.id)}
                          disabled={deletingAccountId === profile.id}
                          className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 bg-gray-900 border border-gray-800 hover:bg-red-900/20 hover:border-red-900/50 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          title={deletingAccountId === profile.id ? 'Deleting...' : 'Delete Account & Data'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
